/** Hero backdrop config — assets + page fill color (matches bottom of each hero image). */

import { getMarketingHeroHeaderTheme } from "./marketingConstants.js";

/** Matches marketing mobile breakpoint (`marketingMobileStyles.js`, hero header). */
export const MARKETING_HERO_BACKDROP_MAX_WIDTH = 900;

/** Solutions hero / Safari top overscroll — white band above the blue wave. */
export const MARKETING_SOLUTIONS_HERO_TOP = "#ffffff";

/**
 * Keys match `MARKETING_PAGE_THEMES`.
 * `shellTopColor` — top of hero image; used on `html` for Safari overscroll above the page.
 * `pageColor` — bottom of hero / page fill; used on the marketing shell background.
 */
export const MARKETING_HERO_BACKDROPS = {
  home: {
    assets: {
      desktop: "/marketing/heroes/home-hero-desktop.svg",
      mobile: "/marketing/heroes/home-hero-mobile.svg",
    },
    shellTopColor: "#006bc2",
    pageColor: "#006bc2",
    pageTone: "dark",
  },
  portals: {
    assets: {
      desktop: "/marketing/heroes/portal-hero-desktop.svg",
      mobile: "/marketing/heroes/portal-hero-mobile.svg",
    },
    shellTopColor: "#001220",
    pageColor: "#a7233a",
    pageTone: "dark",
  },
  shipping: {
    assets: {
      desktop: "/marketing/heroes/shipping-hero-desktop.svg",
      mobile: "/marketing/heroes/shipping-hero-mobile.svg",
    },
    shellTopColor: "#121c2e",
    shellTopColorMobile: "#233553",
    pageColor: "#253559",
    pageColorMobile: "#29344f",
    pageTone: "dark",
  },
  integrations: {
    assets: {
      desktop: "/marketing/heroes/integrations-hero-desktop.svg",
      mobile: "/marketing/heroes/integrations-hero-mobile.svg",
    },
    shellTopColor: "#00cc8e",
    pageColor: "#038373",
    pageTone: "dark",
  },
  pricing: {
    assets: {
      desktop: "/marketing/heroes/pricing-hero-desktop.svg",
      mobile: "/marketing/heroes/pricing-hero-mobile.svg",
    },
    shellTopColor: "#fd5b32",
    pageColor: "#fec8b7",
    pageTone: "light",
  },
  solutions: {
    assets: {
      desktop: "/marketing/heroes/solutions-hero-desktop.svg",
      mobile: "/marketing/heroes/solutions-hero-mobile.svg",
    },
    shellTopColor: MARKETING_SOLUTIONS_HERO_TOP,
    shellTopColorMobile: MARKETING_SOLUTIONS_HERO_TOP,
    pageColor: "#006bc2",
    pageTone: "solutions",
  },
};

export function getMarketingHeroBackdrop(pathname) {
  const theme = getMarketingHeroHeaderTheme(pathname);
  if (!theme) return null;
  return MARKETING_HERO_BACKDROPS[theme] ?? null;
}

export function getMarketingHeroPageColor(config, { mobile = false } = {}) {
  if (!config) return null;
  if (mobile) {
    return config.pageColorMobile ?? config.pageColor ?? null;
  }
  return config.pageColor ?? null;
}

export function getMarketingHeroShellTopColor(config, { mobile = false } = {}) {
  if (!config) return null;
  if (config.pageTone === "solutions") return MARKETING_SOLUTIONS_HERO_TOP;
  if (mobile) {
    return config.shellTopColorMobile ?? config.shellTopColor ?? config.pageColor ?? null;
  }
  return config.shellTopColor ?? config.pageColor ?? null;
}

/** White hero tops need page-fill color on white CTA pills (e.g. solutions). */
export function getMarketingHeroButtonTextColor(config, { mobile = false } = {}) {
  const top = getMarketingHeroShellTopColor(config, { mobile });
  const page = getMarketingHeroPageColor(config, { mobile });
  const norm = String(top || "").trim().toLowerCase();
  if (norm === "#ffffff" || norm === "#fff") return page ?? top;
  return top ?? page ?? null;
}

export function getMarketingHeroPageTone(config) {
  return config?.pageTone ?? "dark";
}

/** Resolved html (top) and body (bottom) overscroll colors for the current viewport. */
export function getMarketingHeroShellColors(config, { mobile = false } = {}) {
  if (!config) return { top: null, bottom: null };
  return {
    top: getMarketingHeroShellTopColor(config, { mobile }),
    bottom: getMarketingHeroPageColor(config, { mobile }),
  };
}

export function isMarketingHeroBackdropMobileViewport() {
  if (typeof window === "undefined") return false;
  return window.matchMedia(`(max-width: ${MARKETING_HERO_BACKDROP_MAX_WIDTH}px)`).matches;
}

export function getMarketingPageScrollTop() {
  if (typeof window === "undefined") return 0;
  return Math.max(0, window.scrollY || document.documentElement.scrollTop || 0);
}

/** Literal hex top colors for Safari overscroll — one rule per hero tab. */
export function buildMarketingHeroOverscrollTopCss() {
  const desktopRules = Object.entries(MARKETING_HERO_BACKDROPS)
    .map(([theme, config]) => {
      const top = getMarketingHeroShellTopColor(config, { mobile: false });
      if (!top) return "";
      return `html.nm-marketing-hero-doc--at-top.nm-marketing-hero-doc--${theme} { background-color: ${top} !important; }`;
    })
    .filter(Boolean)
    .join("\n  ");

  const mobileRules = Object.entries(MARKETING_HERO_BACKDROPS)
    .map(([theme, config]) => {
      const top = getMarketingHeroShellTopColor(config, { mobile: true });
      if (!top) return "";
      return `html.nm-marketing-hero-doc--at-top.nm-marketing-hero-doc--${theme} { background-color: ${top} !important; }`;
    })
    .filter(Boolean)
    .join("\n    ");

  return `${desktopRules}
  @media (max-width: ${MARKETING_HERO_BACKDROP_MAX_WIDTH}px) {
    ${mobileRules}
  }`;
}
